import React, { useState, useEffect } from 'react';
import { financesService } from '../services/financesService';
import { toast } from '../utils/toast';
import { calendarDateKey } from '../utils/financeReports';
import './Finances.css';

const MONTH_SHORT = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
const MONTH_FULL = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

function formatFinanceDate(finance) {
  try {
    const str = calendarDateKey(finance?.date || finance?.created_at);
    if (!str) return '—';
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '—';
    const [, y, m, d] = match;
    const mi = parseInt(m, 10) - 1;
    if (mi < 0 || mi > 11) return '—';
    return `${parseInt(d, 10)} ${MONTH_FULL[mi]} ${y}`;
  } catch (_) {
    return '—';
  }
}

function formatPeriodLabel(period, group) {
  try {
    if (period == null || period === '') return '—';
    const p = String(period).trim();
    if (p === 'undefined' || p.startsWith('undefined')) return '—';
    if (group === 'month') {
      const parts = p.split('-');
      const y = parts[0];
      const m = parts[1];
      if (y == null || m == null || m === 'undefined') return '—';
      const mi = parseInt(m, 10) - 1;
      if (Number.isNaN(mi) || mi < 0 || mi > 11) return '—';
      return `${MONTH_SHORT[mi]}, ${y}`;
    }
    if (group === 'day') {
      const parts = p.split('-');
      const y = parts[0];
      const m = parts[1];
      const d = parts[2];
      if (y == null || m == null || d == null || m === 'undefined') return '—';
      const mi = parseInt(m, 10) - 1;
      if (Number.isNaN(mi) || mi < 0 || mi > 11) return '—';
      return `${parseInt(d, 10)} ${MONTH_SHORT[mi]} ${y}`;
    }
    if (group === 'week') {
      const start = new Date(p);
      if (Number.isNaN(start.getTime())) return p;
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      const mi = start.getMonth();
      if (mi < 0 || mi > 11) return p;
      return `${start.getDate()}–${end.getDate()} ${MONTH_SHORT[mi]} ${String(start.getFullYear()).slice(2)}`;
    }
    return p || '—';
  } catch (_) {
    return period != null ? String(period) : '—';
  }
}

function formatMoney(n) {
  return new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

const Finances = () => {
  const [group, setGroup] = useState('month');
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailEntries, setDetailEntries] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadReports = async () => {
    setLoading(true);
    try {
      const [statsRes, rowsRes] = await Promise.all([
        financesService.getReportsStats(),
        financesService.getByMonth({ group })
      ]);
      const okStats = statsRes && typeof statsRes === 'object' && !Array.isArray(statsRes) && 'income' in statsRes;
      setStats(okStats ? statsRes : { income: 0, expense: 0, balance: 0 });
      // Только строки с валидным периодом (YYYY-MM или YYYY-MM-DD), без дубликатов по period
      const validPeriodRe = group === 'month' ? /^\d{4}-\d{2}$/ : /^\d{4}-\d{2}(-\d{2})?$/;
      const seen = new Set();
      const normalized = (Array.isArray(rowsRes) ? rowsRes : [])
        .filter((r) => r && r.period != null && r.period !== '' && validPeriodRe.test(String(r.period)))
        .filter((r) => {
          if (seen.has(r.period)) return false;
          seen.add(r.period);
          return true;
        })
        .sort((a, b) => (b.period || '').localeCompare(a.period || ''));
      setRows(normalized);
    } catch (e) {
      console.error(e);
      toast.error('Ошибка загрузки отчётов');
      setStats({ income: 0, expense: 0, balance: 0 });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);

  const openDetail = async (period) => {
    setDetail(period);
    setDetailLoading(true);
    setDetailEntries([]);
    try {
      const list = await financesService.getPeriodEntries(period, group);
      setDetailEntries(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setDetailEntries([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetail(null);
    setDetailEntries([]);
  };

  if (detail != null) {
    const detailIncome = detailEntries
      .filter((entry) => entry.type === 'income')
      .reduce((sum, entry) => sum + parseFloat(entry.amount || 0), 0);
    const detailExpense = detailEntries
      .filter((entry) => entry.type === 'expense')
      .reduce((sum, entry) => sum + parseFloat(entry.amount || 0), 0);
    const detailProfit = detailIncome - detailExpense;
    return (
      <div className="reports-page gnom-reports reports-new">
        <header className="reports-new__header">
          <button type="button" className="reports-new__back" onClick={closeDetail} aria-label="Назад">←</button>
          <h1 className="reports-new__title">{formatPeriodLabel(detail, group)}</h1>
          <span className="reports-new__header-right" />
        </header>
        <div className="reports-new__body">
          <p className="reports-new__hint">Только зарегистрированные оплаты и расходы за этот календарный период</p>
          {detailLoading ? (
            <div className="reports-new__loader" />
          ) : detailEntries.length === 0 ? (
            <p className="reports-new__empty">Нет финансовых операций за период</p>
          ) : (
            <ul className="reports-new__task-list">
              {detailEntries.map((entry, i) => (
                <li key={entry.id != null ? entry.id : `detail-${i}`} className="reports-new__task-item">
                  <span className="reports-new__task-date">{formatFinanceDate(entry)}</span>
                  <span className="reports-new__task-title">
                    {entry.task_title || entry.description || entry.category || (entry.type === 'income' ? 'Доход' : 'Расход')}
                  </span>
                  {(entry.client_name || entry.category) && (
                    <span className="reports-new__task-client">{entry.client_name || entry.category}</span>
                  )}
                  <span className={`reports-new__task-sum reports-new__task-sum--${entry.type}`}>
                    {entry.type === 'income' ? '+' : '−'}{formatMoney(parseFloat(entry.amount || 0))} ₽
                  </span>
                </li>
              ))}
            </ul>
          )}
          {!detailLoading && detailEntries.length > 0 && (
            <div className="reports-new__detail-total">
              <span>Приход <strong>+{formatMoney(detailIncome)} ₽</strong></span>
              <span>Расход <strong>−{formatMoney(detailExpense)} ₽</strong></span>
              <span>Прибыль <strong>{formatMoney(detailProfit)} ₽</strong></span>
            </div>
          )}
        </div>
      </div>
    );
  }

  const totalIncome = (stats?.income ?? 0);
  const totalExpense = (stats?.expense ?? 0);
  const totalBalance = (stats?.balance ?? totalIncome - totalExpense);
  const taxRate = Number(stats?.tax_rate ?? 7);
  const taxAmount = Number(stats?.tax_amount ?? totalIncome * taxRate / 100);
  const totalIncomeAfterTax = Number(stats?.income_after_tax ?? totalIncome - taxAmount);

  return (
    <div className="reports-page gnom-reports reports-new">
      <header className="reports-new__header">
        <span className="reports-new__header-left" />
        <h1 className="reports-new__title">Отчёты</h1>
        <span className="reports-new__header-right" />
      </header>

      <div className="reports-new__body">
        <p className="reports-new__hint">Доход — только после отметки «Оплачено». Личные задачи не учитываются.</p>

        <div className="reports-new__cards">
          <div className="reports-new__card reports-new__card--income">
            <span className="reports-new__card-label">Доход за всё время</span>
            <span className="reports-new__card-value">+{formatMoney(totalIncome)} ₽</span>
          </div>
          <div className="reports-new__card reports-new__card--expense">
            <span className="reports-new__card-label">Расход за всё время</span>
            <span className="reports-new__card-value">−{formatMoney(totalExpense)} ₽</span>
          </div>
          <div className="reports-new__card reports-new__card--balance">
            <span className="reports-new__card-label">Баланс за всё время</span>
            <span className="reports-new__card-value">{formatMoney(totalBalance)} ₽</span>
          </div>
          <div className="reports-new__card reports-new__card--net">
            <span className="reports-new__card-label">Доход после налога ({taxRate}%)</span>
            <span className="reports-new__card-value">{formatMoney(totalIncomeAfterTax)} ₽</span>
          </div>
        </div>

        <div className="reports-new__tabs">
          {['day', 'week', 'month'].map((g) => (
            <button
              key={g}
              type="button"
              className={`reports-new__tab ${group === g ? 'reports-new__tab--active' : ''}`}
              onClick={() => setGroup(g)}
            >
              {g === 'day' ? 'День' : g === 'week' ? 'Неделя' : 'Месяц'}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="reports-new__loader reports-new__loader--center" />
        ) : (
          <div className="reports-new__table-wrap">
            <table className="reports-new__table">
              <thead>
                <tr>
                  <th>Период</th>
                  <th className="reports-new__th-num">Приход</th>
                  <th className="reports-new__th-num">Расход</th>
                  <th className="reports-new__th-num">Прибыль</th>
                  <th className="reports-new__th-num">После налога</th>
                </tr>
              </thead>
              <tbody>
                {(rows || []).map((row) => (
                  <tr key={row.period != null ? row.period : row.index}>
                    <td>
                      <button type="button" className="reports-new__period-btn" onClick={() => openDetail(row.period)}>
                        {formatPeriodLabel(row.period != null ? String(row.period) : '', group)}
                      </button>
                    </td>
                    <td className="reports-new__td-num reports-new__td--income">+{formatMoney(row.income || 0)}</td>
                    <td className="reports-new__td-num reports-new__td--expense">−{formatMoney(row.expense || 0)}</td>
                    <td className="reports-new__td-num reports-new__td--profit">{formatMoney(row.profit ?? (row.income || 0) - (row.expense || 0))}</td>
                    <td className="reports-new__td-num reports-new__td--profit">{formatMoney(row.income_after_tax ?? (row.income || 0) * (1 - taxRate / 100))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="reports-new__footer">
                  <td>Всего</td>
                  <td className="reports-new__td-num reports-new__td--income">+{formatMoney(totalIncome)}</td>
                  <td className="reports-new__td-num reports-new__td--expense">−{formatMoney(totalExpense)}</td>
                  <td className="reports-new__td-num reports-new__td--profit">{formatMoney(totalBalance)}</td>
                  <td className="reports-new__td-num reports-new__td--profit">{formatMoney(totalIncomeAfterTax)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Finances;
